import { useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDynamicPermissions } from './useDynamicPermissions';

interface UseUserPermissionsReturn {
  isContactUser: boolean;
  canCreateEntity: (entity: string) => boolean;
  canUpdateEntity: (entity: string) => boolean;
  canDeleteEntity: (entity: string) => boolean;
  canViewEntity: (entity: string) => boolean;
  canAccessMenuItem: (entity: string) => boolean;
  showCreateButton: (entity: string) => boolean;
  showEditButton: (entity: string) => boolean;
  loading: boolean;
}

export function useUserPermissions(): UseUserPermissionsReturn {
  const { user } = useAuth();
  const { can, loading } = useDynamicPermissions();

  const isContactUserMemo = useMemo((): boolean => {
    // Check if user has 'contact' role or class
    if (!user) return false;

    // Check for class field (if exists) - case insensitive check
    if ('class' in user && user.class?.toLowerCase() === 'contact') return true;

    // Check for role-based determination
    if ((user.role as any)?.name === 'contact') return true;

    return false;
  }, [user]);

  // All permission checks now use database permissions only
  const canCreateEntity = useCallback((entity: string): boolean => {
    return can(entity, 'create');
  }, [can]);

  const canUpdateEntity = useCallback((entity: string): boolean => {
    return can(entity, 'update');
  }, [can]);

  const canDeleteEntity = useCallback((entity: string): boolean => {
    return can(entity, 'delete');
  }, [can]);

  const canViewEntity = useCallback((entity: string): boolean => {
    return can(entity, 'read');
  }, [can]);

  const canAccessMenuItem = useCallback((entity: string): boolean => {
    return can(entity, 'read');
  }, [can]);

  const showCreateButton = useCallback((entity: string): boolean => {
    return canCreateEntity(entity);
  }, [canCreateEntity]);

  const showEditButton = useCallback((entity: string): boolean => {
    return canUpdateEntity(entity);
  }, [canUpdateEntity]);

  return {
    isContactUser: isContactUserMemo,
    canCreateEntity,
    canUpdateEntity,
    canDeleteEntity,
    canViewEntity,
    canAccessMenuItem,
    showCreateButton,
    showEditButton,
    loading
  };
}

// Helper HOC for protecting components based on user class
export function withContactUserRestriction<P extends object>(
  Component: React.ComponentType<P>,
  entity: string,
  action: 'create' | 'update' | 'view',
  FallbackComponent?: React.ComponentType
) {
  return function ContactUserRestrictedComponent(props: P) {
    const { canCreateEntity, canUpdateEntity, canViewEntity } = useUserPermissions();

    let hasAccess = false;
    switch (action) {
      case 'create':
        hasAccess = canCreateEntity(entity);
        break;
      case 'update':
        hasAccess = canUpdateEntity(entity);
        break;
      case 'view':
        hasAccess = canViewEntity(entity);
        break;
    }

    if (!hasAccess) {
      if (FallbackComponent) {
        return <FallbackComponent />;
      }
      return (
        <div className="text-center p-4">
          <p className="text-gray-500">You don't have permission to {action} {entity}.</p>
        </div>
      );
    }

    return <Component {...props} />;
  };
}

// Component for conditional rendering based on Contact user restrictions
interface ContactUserGateProps {
  entity: string;
  action: 'create' | 'update' | 'view';
  children: React.ReactNode;
  fallback?: React.ReactNode;
  hideWhenUnauthorized?: boolean;
}

export function ContactUserGate({ 
  entity, 
  action, 
  children, 
  fallback = null,
  hideWhenUnauthorized = true 
}: ContactUserGateProps) {
  const { canCreateEntity, canUpdateEntity, canViewEntity } = useUserPermissions();

  let hasAccess = false;
  switch (action) {
    case 'create':
      hasAccess = canCreateEntity(entity);
      break;
    case 'update':
      hasAccess = canUpdateEntity(entity);
      break;
    case 'view':
      hasAccess = canViewEntity(entity);
      break;
  }

  if (!hasAccess) {
    if (hideWhenUnauthorized) {
      return null;
    }
    return <>{fallback}</>;
  }

  return <>{children}</>;
}